import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
    Heart, MessageCircle, Share2, ExternalLink, Copy, Bell, 
    Clock, Eye, ChevronLeft, Send, Loader2, Check, ThumbsUp, TrendingDown
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { API } from '../config/apiBase';
import { registrar, registrarCompartilhamento, registrarIntencaoCompra } from '../inteligencia/coleta';
import { alternarCurtida } from '../utils/curtida';
import AmazonManualCta from '../components/AmazonManualCta';
import { descontoOficialSemPrecoAnterior, precoAnteriorExibivel } from '../utils/precoAnterior';


export default function DealPage() {
    const { dealId } = useParams();
    const { user, isAuthenticated } = useAuth();
    const { isClassic } = useTheme();
    const [deal, setDeal] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [liked, setLiked] = useState(false);
    const [likesCount, setLikesCount] = useState(0);
    const [inWishlist, setInWishlist] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [alertPrice, setAlertPrice] = useState('');
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [priceHistory, setPriceHistory] = useState([]);

    useEffect(() => {
        fetchDeal();
        fetchPriceHistory();
        if (isAuthenticated) {
            checkWishlist();
        }
    }, [dealId, isAuthenticated]);

    const fetchPriceHistory = async () => {
        try {
            const res = await fetch(`${API}/produtos/${dealId}/price-history`);
            if (res.ok) setPriceHistory(await res.json());
        } catch {}
    };

    const fetchDeal = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API}/produtos/${dealId}`);
            if (!response.ok) throw new Error('Deal not found');
            const data = await response.json();
            setDeal(data.deal);
            setComments(data.comments || []);
            setLikesCount(data.deal.likes || 0);
            // Track deal view (impression) for analytics
            fetch(`${API}/track/event`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ event_type: 'deal_view', deal_id: dealId, category: data.deal?.category_id || '', source: 'web' }),
            }).catch(() => {});
        } catch (error) {
            console.error('Error fetching deal:', error);
            toast.error('Oferta não encontrada');
        } finally {
            setLoading(false);
        }
    };

    const checkWishlist = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API}/wishlist`, {
                credentials: 'include',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            if (response.ok) {
                const data = await response.json();
                setInWishlist(data.wishlist.some(w => w.deal_id === dealId));
            }
        } catch (error) {
            console.error('Error checking wishlist:', error);
        }
    };

    const handleLike = async () => {
        const r = await alternarCurtida({ dealId, autenticado: isAuthenticated, token: localStorage.getItem('token') });
        if (r.estado === 'login') {
            toast.error('Faça login para curtir');
            return;
        }
        if (r.estado === 'ok') {
            // Só curtida confirmada pelo backend vira sinal e mexe no contador.
            registrar(r.curtido ? 'like' : 'unlike', { deal_id: dealId, surface: 'deal_page', marketplace: deal?.store_name || '' });
            setLiked(r.curtido);
            setLikesCount((prev) => Math.max(0, prev + r.delta));
            return;
        }
        toast.error('Erro ao curtir');
    };

    const handleWishlist = async () => {
        if (!isAuthenticated) {
            toast.error('Faça login para adicionar aos desejos');
            return;
        }
        
        const token = localStorage.getItem('token');
        try {
            if (inWishlist) {
                await fetch(`${API}/wishlist/${dealId}`, {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                setInWishlist(false);
                toast.success('Removido da lista de desejos');
            } else {
                await fetch(`${API}/wishlist/${dealId}`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                setInWishlist(true);
                toast.success('Adicionado à lista de desejos');
            }
        } catch (error) {
            toast.error('Erro ao atualizar lista de desejos');
        }
    };

    const handleComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        if (!isAuthenticated) {
            toast.error('Faça login para comentar');
            return;
        }
        
        const token = localStorage.getItem('token');
        setSubmittingComment(true);
        try {
            const response = await fetch(`${API}/comments`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include',
                body: JSON.stringify({ deal_id: dealId, content: newComment })
            });
            
            if (response.ok) {
                const comment = await response.json();
                setComments([comment, ...comments]);
                setNewComment('');
                toast.success('Comentário adicionado!');
            }
        } catch (error) {
            toast.error('Erro ao comentar');
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleCreateAlert = async () => {
        if (!isAuthenticated) {
            toast.error('Faça login para criar alertas');
            return;
        }
        
        const price = parseFloat(alertPrice);
        if (isNaN(price) || price <= 0) {
            toast.error('Informe um preço válido');
            return;
        }
        
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API}/alerts`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                credentials: 'include',
                body: JSON.stringify({ deal_id: dealId, target_price: price })
            });
            
            if (response.ok) {
                toast.success('Alerta criado! Você será notificado quando o preço baixar.');
                setShowAlertModal(false);
                setAlertPrice('');
            }
        } catch (error) {
            toast.error('Erro ao criar alerta');
        }
    };

    const copyLink = () => {
        registrarCompartilhamento(dealId, 'copy_link', 'deal_page', navigator.clipboard.writeText(window.location.href))?.catch(() => {});
        toast.success('Link copiado!');
    };

    const copyCoupon = (code) => {
        navigator.clipboard.writeText(code);
        toast.success('Cupom copiado!');
    };

    const voteStatus = async (vote) => {
        if (!isAuthenticated) {
            toast.error('Faça login para votar');
            return;
        }
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API}/produtos/${dealId}/vote-status`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ vote })
            });
            if (response.ok) {
                const data = await response.json();
                setDeal(prev => ({ 
                    ...prev, 
                    available_votes: data.available_votes, 
                    expired_votes: data.expired_votes,
                    expiration_status: data.expiration_status 
                }));
                toast.success(vote === 'available' ? 'Marcado como disponível!' : 'Marcado como expirado');
            }
        } catch (error) {
            toast.error('Erro ao votar');
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(price);
    };

    // Oferta Amazon manual (SiteStripe): sem preço de fonte oficial. Sem bloco de preço, sem desconto e sem alerta
    // de preço (as Políticas de Associados proíbem alerta e exibir preço que não venha da API oficial).
    const semPreco = deal?.price_mode === 'sem_preco';

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
            </div>
        );
    }

    if (!deal) {
        return (
            <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
                <p className="text-muted-foreground">Oferta não encontrada</p>
                <Link to="/">
                    <Button className="btn-gold">Voltar para Home</Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background theme-transition" data-testid="deal-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back button */}
                <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                    Voltar para ofertas
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Image */}
                    <div className="bg-white rounded-xl p-8 flex items-center justify-center">
                        {semPreco && !deal.image_url ? (
                            <span className="text-2xl font-bold" style={{ color: '#7B8AA6' }} data-testid="deal-image-vazia">Amazon</span>
                        ) : (
                        <img
                            src={deal.image_url}
                            alt={deal.title}
                            className="max-w-full max-h-[400px] object-contain"
                            data-testid="deal-image"
                        />
                        )}
                    </div>

                    {/* Details */}
                    <div className="space-y-6">
                        {/* Store */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground uppercase tracking-wide">{deal.store_name}</span>
                            {deal.is_hot && (
                                <span className="hot-badge text-white font-bold text-xs px-2 py-0.5 rounded">
                                    HOT
                                </span>
                            )}
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl md:text-3xl font-outfit font-bold text-foreground" data-testid="deal-title">
                            {deal.title}
                        </h1>

                        {/* Price */}
                        {semPreco ? (
                            deal.requires_prime === true && (
                                <span className="inline-block text-sm font-semibold px-2.5 py-1 rounded-md" style={{ background: 'var(--status-warning-bg)', color: 'var(--sv-warning)' }}
                                    data-testid="deal-prime">⭐ Oferta para membros Prime</span>
                            )
                        ) : (
                        <div className="space-y-2">
                            <div className="flex items-baseline gap-3">
                                <span className="text-3xl font-bold text-foreground" data-testid="deal-price" style={{ fontFamily: isClassic ? 'Manrope, sans-serif' : 'inherit', fontWeight: 700 }}>
                                    {formatPrice(deal.current_price)}
                                </span>
                                {precoAnteriorExibivel(deal) !== null && (
                                    <>
                                        <span className="text-lg text-muted-foreground line-through" style={{ fontFamily: isClassic ? 'Manrope, sans-serif' : 'inherit', fontWeight: isClassic ? 700 : 400 }}>
                                            {formatPrice(precoAnteriorExibivel(deal))}
                                        </span>
                                        <span className="discount-badge text-black text-sm px-2 py-1 rounded" style={{ fontFamily: isClassic ? 'Manrope, sans-serif' : 'inherit', fontWeight: 700 }}>
                                            -{deal.discount_percent}%
                                        </span>
                                    </>
                                )}
                                {/* Shopee: desconto oficial da API sem preço anterior (o derivado nunca é riscado) */}
                                {descontoOficialSemPrecoAnterior(deal) && (
                                    <span className="discount-badge text-black text-sm px-2 py-1 rounded" data-testid="deal-desconto-oficial" style={{ fontFamily: isClassic ? 'Manrope, sans-serif' : 'inherit', fontWeight: 700 }}>
                                        -{deal.discount_percent}%
                                    </span>
                                )}
                            </div>
                        </div>
                        )}

                        {/* Coupon */}
                        {deal.coupon_code && (
                            <div className="coupon-card p-4 rounded-lg">
                                <p className="text-xs text-muted-foreground mb-2">Use o cupom:</p>
                                <div className="flex items-center gap-3">
                                    <code className="flex-1 bg-background/50 px-4 py-2 rounded text-gold-500 font-mono text-lg">
                                        {deal.coupon_code}
                                    </code>
                                    <Button 
                                        onClick={() => copyCoupon(deal.coupon_code)}
                                        variant="outline"
                                        size="sm"
                                        className="border-gold-500/50 text-gold-500"
                                        data-testid="copy-coupon-btn"
                                    >
                                        <Copy className="w-4 h-4 mr-1" />
                                        Copiar
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* CTA — quem decide se da para comprar e o backend, pelo
                            campo derivado purchase_available. A regra de URL mora em
                            _deal_has_purchase_target, no server, e NAO e duplicada aqui.
                            `=== false` de proposito: payload antigo sem o campo
                            preserva o comportamento anterior. */}
                        {deal.purchase_available === false ? (
                            <div
                                className="flex items-center justify-center gap-2 w-full py-4 rounded-lg text-lg font-semibold cursor-not-allowed"
                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--muted)' }}
                                data-testid="buy-unavailable"
                            >
                                Oferta indisponível
                            </div>
                        ) : semPreco ? (
                            // Amazon manual (SiteStripe): link oficial DIRETO, nunca /api/go/deal nem /r/.
                            <AmazonManualCta deal={deal} className="btn-gold flex items-center justify-center gap-2 w-full py-4 rounded-lg text-lg font-semibold" />
                        ) : (
                            <a
                                href={`${API}/go/deal/${deal.deal_id}`}
                                onClick={(e) => {
                                    // Intenção com id: o mesmo id vai para /api/go/deal e liga os registros.
                                    // Coleta desligada: devolve null e o link fica como sempre foi.
                                    const intentId = registrarIntencaoCompra(deal);
                                    if (intentId) e.currentTarget.href = `${API}/go/deal/${deal.deal_id}?i=${intentId}`;
                                }}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-gold flex items-center justify-center gap-2 w-full py-4 rounded-lg text-lg font-semibold"
                                data-testid="buy-btn"
                            >
                                Ir para {deal.store_name}
                                <ExternalLink className="w-5 h-5" />
                            </a>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleLike}
                                variant="outline"
                                className={`flex-1 ${liked ? 'border-blue-500 text-blue-500' : 'border-border text-muted-foreground'}`}
                                data-testid="like-btn"
                            >
                                <ThumbsUp className={`w-4 h-4 mr-2 ${liked ? 'fill-current' : ''}`} />
                                {likesCount}
                            </Button>
                            <Button
                                onClick={handleWishlist}
                                variant="outline"
                                className={`flex-1 ${inWishlist ? 'border-red-500 text-red-500' : 'border-border text-muted-foreground'}`}
                                data-testid="wishlist-btn"
                            >
                                <Heart className={`w-4 h-4 mr-2 ${inWishlist ? 'fill-current' : ''}`} />
                                {inWishlist ? 'Salvo' : 'Salvar'}
                            </Button>
                            {!semPreco && (
                            <Button
                                onClick={() => setShowAlertModal(true)}
                                variant="outline"
                                className="flex-1 border-border text-muted-foreground"
                                data-testid="alert-btn"
                            >
                                <Bell className="w-4 h-4 mr-2" />
                                Alerta
                            </Button>
                            )}
                            <Button
                                onClick={copyLink}
                                variant="outline"
                                className="flex-1 border-border text-muted-foreground"
                                data-testid="share-btn"
                            >
                                <Share2 className="w-4 h-4 mr-2" />
                                Compartilhar
                            </Button>
                        </div>

                        {/* Meta */}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground pt-4 border-t border-border">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-muted overflow-hidden">
                                    {deal.author_picture ? (
                                        <img src={resolveImageUrl(deal.author_picture)} alt={deal.author_name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-xs text-gold-500">
                                            {deal.author_name?.charAt(0)}
                                        </div>
                                    )}
                                </div>
                                <span>{deal.author_name}</span>
                            </div>
                            <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatDistanceToNow(new Date(deal.created_at), { locale: ptBR })}
                            </span>
                            <span className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                {deal.views || 0} views
                            </span>
                        </div>
                        
                        {/* Expiration Voting */}
                        <div className="mt-4 p-3 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                            <p className="text-xs mb-2" style={{ color: 'var(--muted)' }}>Esta oferta ainda está disponível?</p>
                            <div className="flex items-center gap-2">
                                <button 
                                    onClick={() => voteStatus('available')}
                                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded text-xs font-medium transition-colors"
                                    style={{ 
                                        background: deal.expiration_status !== 'expired' ? 'rgba(34, 197, 94, 0.15)' : 'var(--surface)', 
                                        border: '1px solid rgba(34, 197, 94, 0.3)',
                                        color: 'var(--sv-voto-sim, #22C55E)'
                                    }}
                                >
                                    <Check className="w-3 h-3" /> Disponível ({deal.available_votes || 0})
                                </button>
                                <button 
                                    onClick={() => voteStatus('expired')}
                                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded text-xs font-medium transition-colors"
                                    style={{ 
                                        background: deal.expiration_status === 'expired' ? 'rgba(239, 68, 68, 0.15)' : 'var(--surface)', 
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                        color: 'var(--sv-voto-nao, #EF4444)'
                                    }}
                                >
                                    Expirou ({deal.expired_votes || 0})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Description */}
                {deal.description && (
                    <div className="mt-8 p-6 bg-card rounded-xl border border-border">
                        <h2 className="text-lg font-semibold text-foreground mb-4">Descrição</h2>
                        <p className="text-muted-foreground whitespace-pre-line">{deal.description}</p>
                    </div>
                )}

                {/* Price History Chart */}
                {priceHistory.length > 1 && (
                    <div className="mt-8 p-6 bg-card rounded-xl border border-border" data-testid="price-history-section">
                        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                            <TrendingDown className="w-5 h-5 text-gold-500" />
                            Histórico de Preços ({priceHistory.length} registros)
                        </h2>
                        <PriceChart data={priceHistory} />
                    </div>
                )}

                {/* Comments */}
                <div className="mt-8" data-testid="comments-section">
                    <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
                        <MessageCircle className="w-5 h-5 text-gold-500" />
                        Comentários ({comments.length})
                    </h2>

                    {/* Comment Form */}
                    {isAuthenticated ? (
                        <form onSubmit={handleComment} className="mb-6">
                            <div className="flex gap-3">
                                <input
                                    type="text"
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Escreva um comentário..."
                                    className="flex-1 bg-card border border-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:border-gold-500/50 focus:outline-none"
                                    data-testid="comment-input"
                                />
                                <Button 
                                    type="submit" 
                                    disabled={submittingComment || !newComment.trim()}
                                    className="btn-gold px-6"
                                    data-testid="comment-submit"
                                >
                                    {submittingComment ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </Button>
                            </div>
                        </form>
                    ) : (
                        <div className="mb-6 p-4 bg-card rounded-lg text-center border border-border">
                            <p className="text-muted-foreground">
                                <Link to="/login" className="text-gold-500 hover:underline">Faça login</Link> para comentar
                            </p>
                        </div>
                    )}

                    {/* Comments List */}
                    <div className="space-y-4">
                        {comments.length === 0 ? (
                            <p className="text-muted-foreground text-center py-8">Seja o primeiro a comentar!</p>
                        ) : (
                            comments.map((comment) => {
                                const src = comment.source || 'web';
                                const srcBadge = src === 'telegram' ? { label: 'Telegram', cls: 'bg-blue-500/15 text-blue-400' }
                                    : src === 'whatsapp' ? { label: 'WhatsApp', cls: 'bg-green-500/15 text-green-400' }
                                    : null;
                                const body = comment.text || comment.content || '';
                                const key = comment.comment_id || `${comment.user_id}-${comment.created_at}`;
                                return (
                                <div key={key} className="comment-item py-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-muted overflow-hidden flex-shrink-0">
                                            {comment.user_picture ? (
                                                <img src={resolveImageUrl(comment.user_picture)} alt={comment.user_name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-xs text-gold-500">
                                                    {comment.user_name?.charAt(0) || '?'}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <span className="text-sm font-medium text-foreground">{comment.user_name || 'Anônimo'}</span>
                                                {srcBadge && (
                                                    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${srcBadge.cls}`}>
                                                        via {srcBadge.label}
                                                    </span>
                                                )}
                                                <span className="text-xs text-muted-foreground">
                                                    {formatDistanceToNow(new Date(comment.created_at), { locale: ptBR, addSuffix: true })}
                                                </span>
                                            </div>
                                            <p className="text-muted-foreground text-sm">{body}</p>
                                        </div>
                                    </div>
                                </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* Price Alert Modal */}
            {showAlertModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                    <div className="bg-card rounded-xl p-6 w-full max-w-md mx-4 border border-border">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Criar Alerta de Preço</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Receba uma notificação quando o preço cair para o valor desejado.
                        </p>
                        <p className="text-xs text-muted-foreground mb-2">Preço atual: <span style={{ fontFamily: isClassic ? 'Manrope, sans-serif' : 'inherit', fontWeight: 700 }}>{formatPrice(deal.current_price)}</span></p>
                        <input
                            type="number"
                            value={alertPrice}
                            onChange={(e) => setAlertPrice(e.target.value)}
                            placeholder="Digite o preço alvo (ex: 499.90)"
                            className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder-muted-foreground focus:border-gold-500/50 focus:outline-none mb-4"
                            data-testid="alert-price-input"
                        />
                        <div className="flex gap-3">
                            <Button
                                onClick={() => setShowAlertModal(false)}
                                variant="outline"
                                className="flex-1 border-border text-muted-foreground"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleCreateAlert}
                                className="flex-1 btn-gold"
                                data-testid="create-alert-btn"
                            >
                                Criar Alerta
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function PriceChart({ data }) {
    if (!data || data.length < 2) return null;
    const W = 600, H = 160, PAD = 40;
    const prices = data.map(d => d.price);
    const minP = Math.min(...prices) * 0.95;
    const maxP = Math.max(...prices) * 1.05;
    const rangeP = maxP - minP || 1;
    const xStep = (W - PAD * 2) / (data.length - 1);

    const points = data.map((d, i) => ({
        x: PAD + i * xStep,
        y: PAD + (1 - (d.price - minP) / rangeP) * (H - PAD * 2),
        price: d.price,
        date: new Date(d.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
    }));

    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const areaD = pathD + ` L ${points[points.length - 1].x} ${H - PAD} L ${PAD} ${H - PAD} Z`;

    const formatPrice = (v) => `R$ ${v.toFixed(0)}`;

    return (
        <div className="w-full overflow-x-auto">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 320, maxHeight: 200 }}>
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map(pct => {
                    const y = PAD + (1 - pct) * (H - PAD * 2);
                    const val = minP + pct * rangeP;
                    return (
                        <g key={pct}>
                            <line x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="var(--border)" strokeWidth="0.5" strokeDasharray="4 2" />
                            <text x={PAD - 4} y={y + 3} textAnchor="end" fill="var(--muted-2, #666)" fontSize="8">{formatPrice(val)}</text>
                        </g>
                    );
                })}
                {/* Area fill */}
                <path d={areaD} fill="url(#priceGrad)" opacity="0.3" />
                {/* Line */}
                <path d={pathD} fill="none" stroke="#4E8DF0" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                {/* Dots + labels */}
                {points.map((p, i) => (
                    <g key={i}>
                        <circle cx={p.x} cy={p.y} r="3" fill="#7DB3FF" stroke="var(--sv-grafico-anel, #071A3A)" strokeWidth="1.5" />
                        {(i === 0 || i === points.length - 1 || data.length <= 6) && (
                            <text x={p.x} y={H - PAD + 14} textAnchor="middle" fill="var(--muted-2, #666)" fontSize="7">{p.date}</text>
                        )}
                    </g>
                ))}
                {/* Gradient def */}
                <defs>
                    <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4E8DF0" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#4E8DF0" stopOpacity="0" />
                    </linearGradient>
                </defs>
            </svg>
            {/* Price range labels */}
            <div className="flex justify-between mt-2 text-xs" style={{ color: 'var(--muted-foreground, var(--muted))' }}>
                <span>Min: R$ {Math.min(...prices).toFixed(2)}</span>
                <span>Atual: R$ {prices[prices.length - 1].toFixed(2)}</span>
                <span>Max: R$ {Math.max(...prices).toFixed(2)}</span>
            </div>
        </div>
    );
}
